import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabaseClient'
import { COUNTRY_OPTIONS } from '@/lib/countries'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export default function PortalAddCompany() {
  const { canSeeClients, isSiteAdmin, isLoading: roleLoading, isError: roleError } = usePlatformRoleContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [country, setCountry] = useState('')
  const [entityName, setEntityName] = useState('')
  const [taxId, setTaxId] = useState('')

  const addMutation = useMutation({
    mutationFn: async (payload: { name: string; website_url?: string; country?: string; entity_name?: string; tax_id?: string }) => {
      const { data, error } = await supabase
        .from('businesses')
        .insert({
          name: payload.name,
          website_url: payload.website_url || null,
          country: payload.country || null,
          entity_name: payload.entity_name || null,
          tax_id: payload.tax_id || null,
        })
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      toast.success('Company added')
      navigate('/portal/businesses')
    },
    onError: (err: Error) => {
      toast.error(err.message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    addMutation.mutate({
      name: name.trim(),
      website_url: website.trim() || undefined,
      country: country.trim() || undefined,
      entity_name: entityName.trim() || undefined,
      tax_id: taxId.trim() || undefined,
    })
  }

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  if (roleError || !canSeeClients || !isSiteAdmin) {
    return <Navigate to="/portal" replace />
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">Add company</h1>
        <p className="text-white/60 text-sm mt-1">Create a new company in the platform.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="company-name" className="text-white/80">
              Name <span className="text-amber-400">*</span>
            </Label>
            <Input
              id="company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company name"
              required
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={addMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-website" className="text-white/80">
              Website
            </Label>
            <Input
              id="company-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={addMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-country" className="text-white/80">
              Country
            </Label>
            <select
              id="company-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="select-chevron mt-1.5 w-full h-10 px-4 rounded-lg border border-white/20 bg-white/10 text-white focus:outline-none hover:border-white/30 focus:border-white/40 transition-all disabled:opacity-50"
              disabled={addMutation.isPending}
            >
              <option value="" className="bg-accent text-white">Select country...</option>
              {COUNTRY_OPTIONS.map((countryName) => (
                <option key={countryName} value={countryName} className="bg-accent text-white">{countryName}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="company-entity-name" className="text-white/80">
              Entity name
            </Label>
            <Input
              id="company-entity-name"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="Full legal name of the business"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={addMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="company-tax-id" className="text-white/80">
              Tax ID
            </Label>
            <Input
              id="company-tax-id"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder="Tax ID or government business ID"
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={addMutation.isPending}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              disabled={addMutation.isPending || !name.trim()}
              className="bg-white text-accent hover:bg-white/90"
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adding…
                </>
              ) : (
                'Add company'
              )}
            </Button>
            <Link to="/portal/businesses">
              <Button
                type="button"
                variant="ghost"
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
